import React, { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Button,
  FormLabel,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import { checkData, deleteItem, updateData } from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues } from "../../../constants/Types";
import { SpaPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  SPA_CATEGORY_LINKING_BUTTON_SUFFIX,
  SPA_CATEGORY_LINKING_DND_CODE,
  SPA_CATEGORY_LINKING_DND_NAME,
  SPA_CATEGORY_LINKING_EMPTY_LIST_MESSAGE,
  SPA_CATEGORY_LINKING_FIELD_MAIN_CATEGORY,
  SPA_CATEGORY_LINKING_HEADER_TOOLTIP,
  SPA_CATEGORY_LINKING_LIST_TITLE,
  SPA_CATEGORY_LINKING_MAIN_CATEGORY_LABEL,
  SPA_CATEGORY_LINKING_MAIN_CATEGORY_PLACEHOLDER,
  SPA_CATEGORY_LINKING_MAIN_CATEGORY_TOOLTIP,
  SPA_CATEGORY_LINKING_PAGE_TITLE,
  SPA_CATEGORY_LINKING_SAMPLE_WARNING,
  SPA_CATEGORY_LINKING_SUBCATEGORY_FIELDS,
  SPA_CATEGORY_LINKING_SUBCATEGORY_TOOLTIPS,
  buildSpaCategoryLinkingMainCategoryOptions,
  buildSpaCategoryLinkingPayload,
  buildSpaCategoryLinkingSubCategoryOptionsByLevel,
  emptySpaCategoryLinkingSubcategories,
  getSpaCategoryLinkingSubcategoryLabel,
  getSpaCategoryLinkingSubcategoryPlaceholder,
  parseSpaCategoryLinkingSubcategoriesFromRow,
  spaCategoryLinkingOptionsWithValue,
} from "../../../constants/SpaCategoryLinkingFields";
import {
  SPA_RETAIL_SUB_CATEGORY_LEVELS,
  getSpaRetailSubCategoryLevelLabel,
} from "../../../constants/SpaRetailSubCategoriesFields";

const SpaCategoryLinking = ({
  data,
  fieldValues,
  retailCategories,
  retailSubCategories,
}: {
  data: (value: any) => void;
  fieldValues: any;
  retailCategories?: any[] | null;
  retailSubCategories?: any[] | null;
}) => {
  const [mainCategory, setMainCategory] = useState<string | null>(null);
  const [subcategories, setSubcategories] = useState<(string | null)[]>(
    emptySpaCategoryLinkingSubcategories,
  );
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);

  const mainCategoryOptions = useMemo(
    () => buildSpaCategoryLinkingMainCategoryOptions(retailCategories),
    [retailCategories],
  );

  const subCategoryOptionsByLevel = useMemo(
    () => buildSpaCategoryLinkingSubCategoryOptionsByLevel(retailSubCategories),
    [retailSubCategories],
  );

  useEffect(() => {
    fieldValues && setItems(fieldValues);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const setSubcategoryAt = (index: number, value: string | null) => {
    setSubcategories((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const updateItem = () => {
    setItems((prev) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SpaPropertyEnum.categoryLinking} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setMainCategory(list[SPA_CATEGORY_LINKING_FIELD_MAIN_CATEGORY] ?? null);
      setSubcategories(parseSpaCategoryLinkingSubcategoriesFromRow(list));
    } else {
      setMainCategory(null);
      setSubcategories(emptySpaCategoryLinkingSubcategories());
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const payload = buildSpaCategoryLinkingPayload(mainCategory, subcategories);

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            {SPA_CATEGORY_LINKING_PAGE_TITLE}
            <Tooltip
              title={tooltipComponent(SPA_CATEGORY_LINKING_HEADER_TOOLTIP)}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="16px" flexWrap="wrap">
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>{SPA_CATEGORY_LINKING_MAIN_CATEGORY_LABEL}</FormLabel>
              <Tooltip
                title={tooltipComponent(
                  SPA_CATEGORY_LINKING_MAIN_CATEGORY_TOOLTIP,
                )}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>{" "}
            <Autocomplete
              options={spaCategoryLinkingOptionsWithValue(
                mainCategoryOptions,
                mainCategory,
              )}
              value={mainCategory}
              onChange={(_, v) => setMainCategory(v ?? null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  size="small"
                  placeholder={SPA_CATEGORY_LINKING_MAIN_CATEGORY_PLACEHOLDER}
                />
              )}
              fullWidth
            />
          </Stack>
          {SPA_RETAIL_SUB_CATEGORY_LEVELS.map((level, index) => {
            const levelLabel = getSpaRetailSubCategoryLevelLabel(level);
            const fieldLabel = SPA_CATEGORY_LINKING_SUBCATEGORY_FIELDS[index];
            const value = subcategories[index] ?? null;
            const options = subCategoryOptionsByLevel[index] ?? [];

            return (
              <Stack direction="column" key={level}>
                <Stack direction="row">
                  <FormLabel>
                    {getSpaCategoryLinkingSubcategoryLabel(
                      fieldLabel,
                      levelLabel,
                    )}
                  </FormLabel>
                  <Tooltip
                    title={tooltipComponent(
                      SPA_CATEGORY_LINKING_SUBCATEGORY_TOOLTIPS[index],
                    )}
                  >
                    <InfoIcon fontSize="small" />
                  </Tooltip>
                </Stack>{" "}
                <Autocomplete
                  options={spaCategoryLinkingOptionsWithValue(options, value)}
                  value={value}
                  onChange={(_, v) => setSubcategoryAt(index, v ?? null)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      placeholder={getSpaCategoryLinkingSubcategoryPlaceholder(
                        levelLabel,
                      )}
                    />
                  )}
                  fullWidth
                />
              </Stack>
            );
          })}
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD}{" "}
            {SPA_CATEGORY_LINKING_BUTTON_SUFFIX}
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            {SPA_CATEGORY_LINKING_SAMPLE_WARNING}
          </Typography>
          <Typography variant="h6">
            {SPA_CATEGORY_LINKING_LIST_TITLE}
          </Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={SPA_CATEGORY_LINKING_DND_CODE}
              name={SPA_CATEGORY_LINKING_DND_NAME}
            />
          ) : (
            <Typography variant="body2">
              {SPA_CATEGORY_LINKING_EMPTY_LIST_MESSAGE}
            </Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};
export default SpaCategoryLinking;
